import { FC } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { RowActions, useModal, useToast } from '../../../../../../components'
import { NotaView } from '../../core/_models'
import {
  downloadArquivo,
  IconPaths,
  ModalSize,
  showAlertLoading,
  showError,
  SituacaoNota,
} from '../../../../../../../_metronic/helpers'
import {
  aprovarNota,
  downloadComprovantePagamento,
  downloadComprovantePagamentoByProfissional,
  downloadNota,
  downloadNotaByProfissional,
} from '../../core/_requests'
import { UploadNotaModal } from '../../components/modal/UploadNotaModal'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { RejeitarNotaModal } from '../../components/modal/RejeitarNotaModal'
import { PagarNotaModal } from '../../components/modal/PagarNotaModal'
import { NotaModal } from '../../components/modal/NotaModal'
import { useT } from '_metronic/i18n/Metronici18n'

type IProps = {
  nota: NotaView
}

export const NotaActionsCell: FC<IProps> = ({ nota }) => {
  const { showModal } = useModal()
  const { showToast } = useToast()
  const { refetch } = useQueryResponse()
  const { t } = useT()
  const location = useLocation()
  const navigate = useNavigate()

  const isProfissionalRoute = location.pathname.includes('/profissional')
  const financeiro = !isProfissionalRoute
  const nome = `nota de ${nota.profissional?.nome ?? ''}`.trim()

  const emAnalise = nota.situacao === SituacaoNota.Analise
  const recebida = nota.situacao === SituacaoNota.Nota_Recebida
  const aguardandoPagamento = nota.situacao === SituacaoNota.Aguardando_Pagamento

  const handleAtendimentos = () =>
    navigate(
      isProfissionalRoute
        ? `/financeiro/notas/profissional/atendimentos/${nota.id}`
        : `/financeiro/notas/atendimentos/${nota.id}`
    )

  const aprovarPagamento = async () => {
    try {
      showAlertLoading()
      await aprovarNota(nota.id)
      Swal.close()
      showToast({ variant: 'success', title: t('alert.nota_pagamento') })
      refetch()
    } catch (error) {
      showError(error)
    }
  }

  const baixar = async (tipo: 'nota' | 'comprovante') => {
    try {
      showAlertLoading()
      const arquivo =
        tipo === 'nota'
          ? isProfissionalRoute
            ? await downloadNotaByProfissional(nota.id)
            : await downloadNota(nota.id)
          : isProfissionalRoute
          ? await downloadComprovantePagamentoByProfissional(nota.id)
          : await downloadComprovantePagamento(nota.id)
      await downloadArquivo(arquivo)
      Swal.close()
    } catch (error) {
      showError(error)
    }
  }

  const enviarNota = () =>
    showModal({ title: t('modal.upload_nota'), content: <UploadNotaModal idNota={nota.id} /> })

  const pagar = () =>
    showModal({
      title: t('modal.pagar_nota'),
      content: <PagarNotaModal idNota={nota.id} />,
      size: ModalSize.Medium,
    })

  const rejeitar = () =>
    showModal({
      title: t('modal.rejeitar_nota'),
      content: <RejeitarNotaModal idNota={nota.id} />,
      size: ModalSize.Medium,
    })

  const openModal = (action: 'autorizar' | 'cancelar' | 'adicional') =>
    showModal({
      title: t('modal.nota', { action }),
      content: <NotaModal action={action} idNota={nota.id} />,
    })

  // Mesmas regras de antes, agora por papel e situação: o que não se aplica fica escondido.
  return (
    <RowActions
      itemName={nome}
      primaryAction={{
        label: t('dropdown_item.ver_itens'),
        onClick: handleAtendimentos,
        icon: IconPaths.Search,
      }}
      items={[
        // Profissional: enviar a nota quando o repasse está aguardando.
        {
          label: t('dropdown_item.enviar', { name: t('nota.label.lowercase') }),
          onClick: enviarNota,
          hidden: !(isProfissionalRoute && nota.aguardando),
        },
        // Financeiro: fluxo por situação.
        {
          label: t('dropdown_item.aprovar_pagamento'),
          onClick: aprovarPagamento,
          hidden: !(financeiro && recebida),
        },
        {
          label: t('dropdown_item.autorizar'),
          onClick: () => openModal('autorizar'),
          hidden: !(financeiro && emAnalise),
        },
        {
          label: t('dropdown_item.atualizar_valor_adicional'),
          onClick: () => openModal('adicional'),
          hidden: !(financeiro && emAnalise),
        },
        {
          label: t('dropdown_item.pagar'),
          onClick: pagar,
          hidden: !(financeiro && aguardandoPagamento),
        },
        // Arquivos.
        {
          label: t('dropdown_item.baixar', { name: t('nota.label.lowercase') }),
          onClick: () => baixar('nota'),
          hidden: !nota.anexado,
        },
        {
          label: t('dropdown_item.baixar', { name: t('comprovante.label.lowercase') }),
          onClick: () => baixar('comprovante'),
          hidden: !nota.pagamentoAnexado,
        },
        // Destrutivas (abrem modal com motivo antes de executar).
        {
          label: t('dropdown_item.rejeitar', { name: t('nota.label.lowercase') }),
          onClick: rejeitar,
          danger: true,
          divider: true,
          hidden: !(financeiro && recebida),
        },
        {
          label: t('dropdown_item.cancelar'),
          onClick: () => openModal('cancelar'),
          danger: true,
          divider: true,
          hidden: !(financeiro && emAnalise),
        },
      ]}
    />
  )
}
